import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, LogOut } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { usePortal } from "@/context/PortalContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { roleInPortal } from "@/lib/roles";
import { Btn, Card, CategoryIcon, Loader } from "@/components/portal/ui";
import { DynamicFields } from "@/components/portal/DynamicFields";
import { LanguageSwitcher } from "@/components/portal/LanguageSwitcher";

// Onboarding step 2 — provider profile completion after registration. Config-driven
// (categories, locations, profile_fields) and themed via the active portal.
export default function Onboarding() {
  const { portalKey, portal, categories, locations, config } = usePortal();
  const { user, loading, logout } = useAuth();
  const { t, L, lang } = useI18n();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_name: "", category_ids: [], location_id: "", description: {},
    contact_phone: "", website: "", fields: {},
  });

  const role = user ? roleInPortal(user, portalKey) : null;

  useEffect(() => {
    if (loading || !user) return;
    if (role !== "provider") { navigate(`/p/${portalKey}/dashboard`, { replace: true }); return; }
    (async () => {
      try {
        const { data } = await api.get("/providers/me");
        if (data && data.business_name) { navigate(`/p/${portalKey}/dashboard`, { replace: true }); return; }
      } catch { /* no profile yet */ }
      setChecking(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, role]);

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader /></div>;
  if (!user) return <Navigate to={`/p/${portalKey}/auth`} replace />;
  if (checking) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader /></div>;

  const toggleCat = (id) => setForm((f) => ({ ...f, category_ids: f.category_ids.includes(id) ? f.category_ids.filter((x) => x !== id) : [...f.category_ids, id] }));
  const st = { background: "var(--surface)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  const fieldCls = "w-full px-3.5 h-11 text-sm border outline-none";

  const finish = async () => {
    if (!form.business_name.trim()) return toast.error(t("profile.businessName"));
    setSaving(true);
    try {
      await api.post("/providers", form);
      toast.success(t("common.saved"));
      navigate(`/p/${portalKey}/dashboard`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }} data-testid="onboarding">
      <header className="h-16 flex items-center border-b" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="w-full max-w-3xl mx-auto px-5 flex items-center gap-3">
          <span className="font-heading text-xl font-bold" style={{ color: "var(--text)" }}>{portal?.name}</span>
          <div className="ml-auto flex items-center gap-3">
            <LanguageSwitcher />
            <Btn variant="ghost" onClick={logout} className="h-10 px-3" data-testid="onb-logout"><LogOut className="h-4 w-4" /></Btn>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>
          <CheckCircle2 className="h-4 w-4" /> {t("onb.step")}
        </div>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold" style={{ color: "var(--text)" }}>{t("onb.title")}</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>{t("onb.subtitle")}</p>

        <Card className="mt-7 p-6 space-y-5" data-testid="onb-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.businessName")}</label>
            <input data-testid="onb-name" className={fieldCls} style={st} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.categories")}</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {categories.map((c) => {
                const on = form.category_ids.includes(c.id);
                return (
                  <button key={c.id} type="button" data-testid={`onb-cat-${c.slug}`} onClick={() => toggleCat(c.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border"
                    style={{ borderRadius: "var(--radius)", borderColor: on ? "var(--accent)" : "var(--border)", background: on ? "var(--accent)" : "transparent", color: on ? "var(--accent-fg)" : "var(--text)" }}>
                    <CategoryIcon name={c.icon} className="h-3.5 w-3.5" /> {L(c.name)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.location")}</label>
              <select data-testid="onb-location" className={fieldCls} style={st} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                <option value="">—</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{L(l.name)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.phone")}</label>
              <input data-testid="onb-phone" className={fieldCls} style={st} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.description")} ({lang.toUpperCase()})</label>
            <textarea data-testid="onb-description" rows={3} className="w-full px-3.5 py-2.5 text-sm border outline-none" style={st}
              value={form.description[lang] || ""} onChange={(e) => setForm({ ...form, description: { ...form.description, [lang]: e.target.value } })} />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.website")}</label>
            <input data-testid="onb-website" className={fieldCls} style={st} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </div>

          {config?.profile_fields?.length > 0 && (
            <DynamicFields fields={config.profile_fields} values={form.fields} onChange={(k, v) => setForm((f) => ({ ...f, fields: { ...f.fields, [k]: v } }))} />
          )}

          <div className="flex items-center gap-3 pt-2">
            <Btn onClick={finish} disabled={saving} data-testid="onb-finish" className="flex-1">{t("onb.finish")}</Btn>
            <Btn variant="ghost" onClick={() => navigate(`/p/${portalKey}/dashboard`, { replace: true })} data-testid="onb-later">{t("onb.later")}</Btn>
          </div>
        </Card>
      </main>
    </div>
  );
}
